import { useEffect, useState } from 'react';
import { Plus, Server, Trash2 } from 'lucide-react';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import {
  Input,
  Switch,
  Button,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useClusterPool,
  useUpdatePool,
  useCreatePool,
  useK8sConfig,
  type K8sPool,
} from '@/api/k8s';
import { POOL_TYPES, randomSlug } from '../cluster-create/cluster-draft';
import { DescList, DescItem } from '../../components/DescList';

const TAINT_EFFECTS = ['NoSchedule', 'PreferNoSchedule', 'NoExecute'];
const reserved = (k: string) => /^(kubernetes\.io|k8s\.io)\//.test(k.trim());

// Shared inline styles for the dialog form rows (label toggles, editor headers, grids).
const switchRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' };
const editorHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--vn-space-2)' };
const editorTitle: React.CSSProperties = { fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' };
const twoCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' };

interface KV { key: string; value: string }
interface Taint { key: string; value: string; effect: string }
const toKV = (v: unknown): KV[] => {
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' && x ? { key: String((x as KV).key ?? ''), value: String((x as KV).value ?? '') } : { key: String(x), value: '' }));
  if (v && typeof v === 'object') return Object.entries(v as Record<string, string>).map(([key, value]) => ({ key, value: String(value) }));
  return [];
};

/**
 * The cluster-autoscaler only activates when this label is present on the pool and
 * its value mirrors the autoscaling flag. Inject it on submit so toggling autoscaling
 * actually takes effect (legacy @console manages it the same way).
 */
const AUTOSCALER_LABEL_KEY = 'autoscaler.k8s.io.infra.vnetwork.dev/enabled';
const withAutoscalerLabel = (labels: KV[], autoscaling: boolean): KV[] => [
  ...labels.filter((l) => l.key && l.key !== AUTOSCALER_LABEL_KEY),
  { key: AUTOSCALER_LABEL_KEY, value: autoscaling ? 'true' : 'false' },
];

// ---- Detail ----
export function PoolDetailDialog({ serviceId, clusterId, pool, onClose }: { serviceId: string; clusterId: string; pool: K8sPool; onClose: () => void }) {
  const { data: full } = useClusterPool(serviceId, clusterId, pool.id);
  const p = full ?? pool;
  const flavor = p.metadata?.flavor ?? p.instanceType;
  const roles = Array.isArray(p.roles) ? p.roles.join(', ') : p.roles;
  const machines = p.machines ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{p.name || p.id}<StatusPill tone={/ready|running/i.test(p.status ?? '') ? 'success' : 'muted'}>{p.status || '—'}</StatusPill></span></DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DescList minColumn={180}>
            <DescItem label="Roles">{roles || '—'}</DescItem>
            <DescItem label="Flavor">{flavor || '—'}</DescItem>
            <DescItem label="Resources">{p.cores ?? '?'} vCPU · {p.memory ?? '?'} GB · {p.volumes ?? '?'} GB disk</DescItem>
            <DescItem label="OS">{p.metadata?.image?.name || '—'}</DescItem>
            {/* Prefer actualMinSize/actualMaxSize: backend zeroes minSize/maxSize for manual pools. */}
            <DescItem label="Sizing">min {p.actualMinSize ?? p.min ?? p.minSize ?? '?'} / max {p.actualMaxSize ?? p.max ?? p.maxSize ?? '?'}{p.currentSize != null ? ` · current ${p.currentSize}` : ''}</DescItem>
            <DescItem label="Scaling">{p.autoscaling ? 'Autoscaling' : 'Manual'}</DescItem>
            <DescItem label="Nodes">{p.totalNodes ?? machines.length}</DescItem>
            <DescItem label="ID"><code style={{ fontSize: 'var(--vn-font-size-sm)' }}>{p.id}</code></DescItem>
          </DescList>
          <div style={{ marginTop: 'var(--vn-space-4)' }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>Machines ({machines.length})</div>
            {machines.length === 0 ? <EmptyState icon={<Server size={20} />} title="No machines yet" description="Machines appear once the pool is provisioned." /> : (
              <div style={{ display: 'grid', gap: 4 }}>{machines.map((m) => <code key={m} style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{m}</code>)}</div>
            )}
          </div>
        </DialogBody>
        <DialogFooter><Button variant="secondary" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Edit ----
export function PoolEditDialog({ serviceId, clusterId, pool, onClose }: { serviceId: string; clusterId: string; pool: K8sPool; onClose: () => void }) {
  const { show } = useToast();
  const { data: full } = useClusterPool(serviceId, clusterId, pool.id);
  const p = full ?? pool;
  const [name, setName] = useState(p.name ?? '');
  const [description, setDescription] = useState(p.description ?? '');
  const [autoscaling, setAutoscaling] = useState(!!p.autoscaling);
  // Prefer actualMinSize/actualMaxSize: backend zeroes minSize/maxSize for manual pools.
  const [min, setMin] = useState(String(p.actualMinSize ?? p.min ?? p.minSize ?? 1));
  const [max, setMax] = useState(String(p.actualMaxSize ?? p.max ?? p.maxSize ?? 3));
  const [labels, setLabels] = useState<KV[]>(toKV(p.labels ?? p.metadata?.labels));
  const [taints, setTaints] = useState<Taint[]>((p.taints ?? []).map((t) => ({ key: t.key, value: t.value ?? '', effect: t.effect })));

  const update = useUpdatePool(serviceId, clusterId, pool.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Pool updated' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });

  // Manual mode → max locked to min.
  const effMax = autoscaling ? Number(max) : Number(min);
  const hasReserved = [...labels, ...taints].some((x) => x.key && reserved(x.key));
  const submit = () => update.mutate({
    // Legacy s10015 update permits camelCase minSize/maxSize; min/max are dropped
    // (sizes silently never change). Sizes come from the form's min + locked max.
    name: name.trim(), description, autoscaling, minSize: Number(min), maxSize: effMax,
    // Backend expects labels as "key=value" strings and locates the autoscaler
    // control label by exact string match — sending {key,value} objects yields
    // Autoscaler.Label.Required. Filter empties, inject the control label, stringify.
    labels: withAutoscalerLabel(labels.filter((l) => l.key.trim() && l.value.trim()), autoscaling).map((l) => `${l.key}=${l.value}`),
    // Update overwrites metadata.tags with the request value (|| []); this dialog has
    // no tags editor, so pass existing tags through to avoid wiping them on a size edit.
    tags: toKV(p.tags).filter((t) => t.key.trim()),
    taints: taints.filter((t) => t.key),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>Edit {p.name || p.id}</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={twoCol}>
              <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
              <Field label="Description">{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />}</Field>
            </div>
            <label style={switchRow}>
              <Switch checked={autoscaling} onCheckedChange={setAutoscaling} /> Autoscaling
            </label>
            <div style={twoCol}>
              <Field label="Min nodes">{(p) => <Input {...p} type="number" min={0} value={min} onChange={(e) => setMin(e.target.value)} />}</Field>
              <Field label="Max nodes">{(p) => <Input {...p} type="number" min={1} value={autoscaling ? max : min} disabled={!autoscaling} onChange={(e) => setMax(e.target.value)} />}</Field>
            </div>

            <KVEditor title="Labels" items={labels} onChange={setLabels} />
            <TaintsEditor items={taints} onChange={setTaints} />
            {hasReserved && <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-warning-text, var(--vn-warning-solid))' }}>Warning: keys with kubernetes.io/ or k8s.io/ prefix are reserved.</p>}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || update.isPending} onClick={submit}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KVEditor({ title, items, onChange }: { title: string; items: KV[]; onChange: (i: KV[]) => void }) {
  const set = (i: number, patch: Partial<KV>) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div>
      <div style={editorHead}>
        <span style={editorTitle}>{title}</span>
        <Button size="sm" variant="ghost" startIcon={<Plus size={13} />} onClick={() => onChange([...items, { key: '', value: '' }])}>Add</Button>
      </div>
      {items.map((x, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--vn-space-2)', marginBottom: 6 }}>
          <Input placeholder="key" value={x.key} onChange={(e) => set(i, { key: e.target.value })} />
          <Input placeholder="value" value={x.value} onChange={(e) => set(i, { value: e.target.value })} />
          <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={13} /></IconButton>
        </div>
      ))}
    </div>
  );
}

function TaintsEditor({ items, onChange }: { items: Taint[]; onChange: (i: Taint[]) => void }) {
  const set = (i: number, patch: Partial<Taint>) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div>
      <div style={editorHead}>
        <span style={editorTitle}>Taints</span>
        <Button size="sm" variant="ghost" startIcon={<Plus size={13} />} onClick={() => onChange([...items, { key: '', value: '', effect: 'NoSchedule' }])}>Add</Button>
      </div>
      {items.map((x, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr auto', gap: 'var(--vn-space-2)', marginBottom: 6 }}>
          <Input placeholder="key" value={x.key} onChange={(e) => set(i, { key: e.target.value })} />
          <Input placeholder="value" value={x.value} onChange={(e) => set(i, { value: e.target.value })} />
          <Select value={x.effect} onValueChange={(v) => set(i, { effect: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{TAINT_EFFECTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
          <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={13} /></IconButton>
        </div>
      ))}
    </div>
  );
}

// ---- Create (full) ----
export function PoolCreateDialog({ serviceId, clusterId, clusterUid, open, onClose }: { serviceId: string; clusterId: string; clusterUid?: string; open: boolean; onClose: () => void }) {
  const { show } = useToast();
  const { data: config } = useK8sConfig(serviceId);
  const flavors = config?.flavors ?? [];
  const gpuModels = config?.gpuModels ?? [];
  // Nodepool name is auto-derived `<clusterUid>-<slug>` and read-only — same logic as
  // the cluster-create wizard (PoolsStep). A fresh slug is generated each time the
  // dialog opens so repeat creates don't collide. The add-pool backend uses the name
  // as-is (no uid prefix, unlike cluster-create), so we send the full derived name.
  const [slug, setSlug] = useState(randomSlug);
  useEffect(() => { if (open) setSlug(randomSlug()); }, [open]);
  const poolName = clusterUid ? `${clusterUid}-${slug}` : slug;
  const [type, setType] = useState('standard');
  const [flavor, setFlavor] = useState('');
  const [enableGpu, setEnableGpu] = useState(false);
  const [gpuType, setGpuType] = useState('');
  const [gpuCount, setGpuCount] = useState(1);
  const [min, setMin] = useState('1');
  const [max, setMax] = useState('3');
  const [volumes, setVolumes] = useState('30');
  const [autoscaling, setAutoscaling] = useState(false);

  const create = useCreatePool(serviceId, clusterId, {
    onSuccess: () => { show({ tone: 'success', title: 'Pool created' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });
  const visible = flavors.filter((f) => (f.type ?? 'standard') === type && (f.cores ?? 0) >= 2 && !f.disabled);
  const valid = !!flavor && (!enableGpu || gpuType);

  // Legacy s10015 create requires the payload wrapped in `pool`, looks the flavor up
  // by id, reads storage from volume.size, and builds the autoscaler/GPU labels itself
  // (request labels ignored). A flat body fails params.require(:pool); flavor-by-name
  // fails the flavor lookup; volumes:<n> leaves storage at 0.
  const submit = () => create.mutate({
    pool: {
      name: poolName,
      flavor,
      runtime: 'containerd',
      min: Number(min), max: Number(max),
      autoscaling,
      volume: { name: 'system', size: Number(volumes), type: 'hiops' },
      ...(enableGpu && gpuType ? { gpu: { type: gpuType, count: gpuCount } } : {}),
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ maxWidth: 560 }}>
        <DialogHeader><DialogTitle>New node pool</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={twoCol}>
              {/* Derived + read-only — auto-generated from the cluster uid, same as the
                  cluster-create wizard (PoolsStep). */}
              <Field label="Name">{(p) => <Input {...p} value={poolName} disabled readOnly />}</Field>
              <Field label="Type">
                {(p) => (
                  <Select value={type} onValueChange={(v) => { setType(v); setFlavor(''); }}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>{POOL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            </div>
            <Field label="Flavor">
              {(p) => (
                <Select value={flavor} onValueChange={setFlavor}>
                  <SelectTrigger {...p}><SelectValue placeholder="Select a flavor" /></SelectTrigger>
                  <SelectContent>{visible.map((f) => <SelectItem key={f.id ?? f.name} value={f.id ?? f.name}>{f.name}{f.cores ? ` — ${f.cores} vCPU, ${f.memory} GB` : ''}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </Field>
            <label style={switchRow}>
              <Switch checked={enableGpu} onCheckedChange={setEnableGpu} /> Enable GPU
            </label>
            {enableGpu && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--vn-space-3)' }}>
                <Select value={gpuType} onValueChange={setGpuType}>
                  <SelectTrigger><SelectValue placeholder="GPU model" /></SelectTrigger>
                  <SelectContent>{gpuModels.map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.id}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(gpuCount)} onValueChange={(v) => setGpuCount(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Min">{(p) => <Input {...p} type="number" min={1} value={min} onChange={(e) => setMin(e.target.value)} />}</Field>
              <Field label="Max">{(p) => <Input {...p} type="number" min={1} value={max} onChange={(e) => setMax(e.target.value)} />}</Field>
              <Field label="Storage (GiB)">{(p) => <Input {...p} type="number" min={30} max={240} value={volumes} onChange={(e) => setVolumes(e.target.value)} />}</Field>
            </div>
            <label style={switchRow}>
              <Switch checked={autoscaling} onCheckedChange={setAutoscaling} /> Autoscaling
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!valid || create.isPending} onClick={submit}>Create pool</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

